import { Timestamp } from 'firebase/firestore';

/**
 * Unified timestamp conversion utilities
 * Provides consistent conversion between Firestore Timestamps, Date objects, and ISO strings
 */

/**
 * Type for any timestamp-like value that can be converted
 */
export type TimestampLike = Timestamp | Date | string | number | null | undefined | {
  toDate?: () => Date;
  toMillis?: () => number;
  seconds?: number;
  nanoseconds?: number;
};

/**
 * Converts any timestamp format to a Date object (for UI/calculations)
 * This is the standard conversion for UI components and date calculations
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns Date object or null if conversion fails
 * 
 * @example
 * ```ts
 * const date = toDate(workStream.startDate); // Date object for UI
 * const age = new Date().getTime() - date.getTime();
 * ```
 */
export function toDate(timestamp: TimestampLike): Date | null {
  if (!timestamp) return null;
  
  // Already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // Firestore Timestamp with toDate method
  if (timestamp && typeof (timestamp as any).toDate === 'function') {
    return (timestamp as any).toDate();
  }
  
  // Firestore Timestamp with toMillis method
  if (timestamp && typeof (timestamp as any).toMillis === 'function') {
    return new Date((timestamp as any).toMillis());
  }
  
  // Firestore format with seconds/nanoseconds
  if (typeof timestamp === 'object' && 'seconds' in timestamp && timestamp.seconds !== undefined) {
    const ts = timestamp as { seconds: number; nanoseconds?: number };
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
  }
  
  // ISO string
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Number (milliseconds)
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  return null;
}

/**
 * Converts any timestamp format to ISO string (for JSON serialization/exports)
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns ISO string or undefined if conversion fails
 * 
 * @example
 * ```ts
 * const iso = toISOString(workStream.createdAt); // "2023-01-01T00:00:00.000Z"
 * JSON.stringify({ createdAt: iso }); // Safe for JSON serialization
 * ```
 */
export function toISOString(timestamp: TimestampLike): string | undefined {
  const date = toDate(timestamp);
  return date ? date.toISOString() : undefined;
}

/**
 * Safe conversion that returns null instead of undefined for consistency
 * Alias for toDate() - provided for clarity when null is preferred over undefined
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns Date object or null if conversion fails
 */
export function toDateOrNull(timestamp: TimestampLike): Date | null {
  return toDate(timestamp);
}

/**
 * Converts timestamp to Date or returns undefined (for optional date fields)
 * Replaces the common pattern: toDate(timestamp) || undefined
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns Date object or undefined if conversion fails
 * 
 * @example
 * ```ts
 * // Before:
 * scheduledDate: toDate(newsletter.scheduledDate) || undefined
 * 
 * // After:
 * scheduledDate: toDateOrUndefined(newsletter.scheduledDate)
 * ```
 */
export function toDateOrUndefined(timestamp: TimestampLike): Date | undefined {
  return toDate(timestamp) || undefined;
}

/**
 * Converts timestamp to Date or returns current date as fallback
 * Replaces the common pattern: toDate(timestamp) || new Date()
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns Date object (current date if conversion fails)
 * 
 * @example
 * ```ts
 * // Before:
 * date: toDate(workshop.date) || new Date()
 * 
 * // After:
 * date: toDateOrNow(workshop.date)
 * ```
 */
export function toDateOrNow(timestamp: TimestampLike): Date {
  return toDate(timestamp) || new Date();
}

/**
 * Checks if a timestamp represents a date in the past
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns true if date is in the past, false otherwise (or if invalid)
 * 
 * @example
 * ```ts
 * // Before:
 * d.dueDate && (() => {
 *   const dueDate = toDate(d.dueDate);
 *   return dueDate && dueDate < new Date();
 * })()
 * 
 * // After:
 * d.dueDate && isDateInPast(d.dueDate)
 * ```
 */
export function isDateInPast(timestamp: TimestampLike): boolean {
  const date = toDate(timestamp);
  if (!date) return false;
  return date < new Date();
}

/**
 * Checks if a timestamp represents a date in the future
 * 
 * @param timestamp - Firestore Timestamp, Date, ISO string, number (milliseconds), or null/undefined
 * @returns true if date is in the future, false otherwise (or if invalid)
 * 
 * @example
 * ```ts
 * // Before:
 * const workshopDate = toDate(w.date);
 * const isFuture = workshopDate && workshopDate > new Date();
 * 
 * // After:
 * const isFuture = isDateInFuture(w.date);
 * ```
 */
export function isDateInFuture(timestamp: TimestampLike): boolean {
  const date = toDate(timestamp);
  if (!date) return false;
  return date > new Date();
}

/**
 * Converts Date or ISO string to Firestore Timestamp (for saving to Firestore)
 * 
 * @param date - Date object, ISO string, or null/undefined
 * @returns Firestore Timestamp or undefined if conversion fails
 * 
 * @example
 * ```ts
 * const timestamp = toTimestamp(new Date()); // Firestore Timestamp
 * await saveToFirestore({ createdAt: timestamp });
 * ```
 */
export function toTimestamp(date: Date | string | null | undefined): Timestamp | undefined {
  if (!date) return undefined;
  
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return undefined;
    return Timestamp.fromDate(date);
  }
  
  if (typeof date === 'string') {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return undefined;
    return Timestamp.fromDate(dateObj);
  }
  
  return undefined;
}

/**
 * Converts Date or timestamp to ISO date string for HTML date inputs (YYYY-MM-DD)
 * 
 * @param date - Date object, ISO string, timestamp, or null/undefined
 * @returns Date string in YYYY-MM-DD format or empty string if conversion fails
 * 
 * @example
 * ```ts
 * const dateStr = toDateInputString(workStream.startDate); // "2023-01-01"
 * <input type="date" value={dateStr} />
 * ```
 */
export function toDateInputString(date: TimestampLike): string {
  const dateObj = toDate(date);
  if (!dateObj) return '';
  return dateObj.toISOString().split('T')[0];
}

/**
 * Converts Date or timestamp to a local datetime string for HTML datetime-local inputs (YYYY-MM-DDTHH:mm).
 * Uses local calendar components — not UTC — because datetime-local values are timezone-naive local times.
 * 
 * @param date - Date object, ISO string, timestamp, or null/undefined
 * @returns Datetime string in YYYY-MM-DDTHH:mm format or empty string if conversion fails
 * 
 * @example
 * ```ts
 * const datetimeStr = toDateTimeInputString(document.dueDate); // "2023-01-01T14:30"
 * <input type="datetime-local" value={datetimeStr} />
 * ```
 */
export function toDateTimeInputString(date: TimestampLike): string {
  const dateObj = toDate(date);
  if (!dateObj) return '';
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Creates a comparator function for sorting arrays by a date field
 * Replaces the common pattern: .sort((a, b) => { const dateA = toDate(a.field); const dateB = toDate(b.field); ... })
 * 
 * @param getTimestamp - Function to extract the timestamp from each item
 * @param order - Sort order: 'desc' (newest first) or 'asc' (oldest first). Default: 'desc'
 * @returns Comparator function for use with Array.sort()
 * 
 * @example
 * ```ts
 * // Before:
 * communications.sort((a, b) => {
 *   const dateA = toDate(a.createdAt);
 *   const dateB = toDate(b.createdAt);
 *   const timeA = dateA ? dateA.getTime() : 0;
 *   const timeB = dateB ? dateB.getTime() : 0;
 *   return timeB - timeA;
 * })
 * 
 * // After:
 * communications.sort(sortByDate(item => item.createdAt, 'desc'))
 * ```
 */
export function sortByDate<T>(
  getTimestamp: (item: T) => TimestampLike,
  order: 'asc' | 'desc' = 'desc'
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const dateA = toDate(getTimestamp(a));
    const dateB = toDate(getTimestamp(b));
    const timeA = dateA ? dateA.getTime() : 0;
    const timeB = dateB ? dateB.getTime() : 0;
    return order === 'desc' ? timeB - timeA : timeA - timeB;
  };
}
